/**
 * System sample letter templates.
 * Appointment sample is adapted from the company Appointment Letter.docx,
 * mapped to PaceHRM {{tags}}. Other types follow the same formal structure.
 * `sampleVersion` bumps force-refresh stored copies of these ids (user uploads untouched).
 */
export const LETTER_SAMPLES_VERSION = 4;

const commonAssignment = {
  branch: ["main-branch"],
  department: ["hr", "it", "finance", "marketing", "sales"],
  designation: ["manager", "executive", "senior-executive", "associate"],
  grade: ["grade-a", "grade-b", "grade-c", "grade-d"],
};

const signOff = `Yours sincerely,
For {{company_name}}

{{hr_manager_name}}
Human Resources

Acknowledged by: ___________________________
Employee Signature: ___________________________
Date: ___________________________`;

export const DEFAULT_LETTER_TEMPLATES = {
  appointment: [
    {
      id: "appointment-1",
      name: "Appointment Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/APT/{{employee_id}}          Date: {{appointment_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of APPOINTMENT

Dear {{employee_name}} ({{employee_id}}),

With reference to your acceptance of the Offer Letter dated {{joining_date}}, we are pleased to confirm your appointment with {{company_name}} (hereinafter "the Company") on the terms and conditions set out herein.

Appointment Details

You are hereby appointed to the position of {{designation}} in the {{department}} department, with effect from {{appointment_date}}. Your place of posting shall be {{work_location}} and you shall report to {{reporting_manager}}.

Compensation & Salary Structure

Your Monthly Gross salary shall be ₹{{gross_salary}}, with an Annual CTC of ₹{{total_ctc}}. The component-wise salary breakup is set out below (Annexure A):

{{salary_structure_table}}

Note: Statutory deductions (Employee PF, ESI, Professional Tax, TDS) shall be deducted from gross salary as applicable under law.

Statutory Benefits

You shall be covered under the Employees' Provident Fund (EPF) scheme in accordance with the Employees' Provident Funds & Miscellaneous Provisions Act, 1952, and the Employees' State Insurance (ESI) scheme in accordance with the Employees' State Insurance Act, 1948, as applicable based on your salary and eligibility criteria under the respective statutes. Both employee and employer contributions shall be made as prescribed under the applicable law and shall be reflected in your monthly salary statement.

Probation & Confirmation

You shall be on probation for a period of {{probation_period}} months from your date of joining. During probation, either party may terminate employment by giving written notice as per company policy. Confirmation in service shall be communicated in writing upon satisfactory assessment.

Notice Period Post Confirmation

Upon confirmation in service, either party shall be required to give {{notice_period}} days written notice to terminate employment or pay the equivalent salary in lieu thereof.

EMPLOYMENT AGREEMENT

Nature of Employment

1.1. Your employment with the Company shall be on a Full-Time Employment basis and governed by the terms of this Agreement, the Company's HR Policies, Code of Conduct, and all applicable policies, rules, and procedures as amended from time to time.

Your employment is subject to satisfactory completion of the probation period, verification of educational and experience credentials, and successful background verification conducted by the Company.

The Company reserves the right to alter, amend, modify, or revoke any policy or term of employment in accordance with business requirements and applicable law, with appropriate notice wherever required.

Duties & Responsibilities

You shall diligently perform the duties and responsibilities assigned to you from time to time, commensurate with your designation and the Company's business requirements.

You shall devote your entire working time, attention, and abilities exclusively to the Company's business. You shall not undertake any other employment, paid or unpaid, or hold any directorship without prior written consent of the Company.

You shall comply with all lawful and reasonable instructions, policies, and directives issued by the Company's management and shall represent the Company's interests at all times.

Working Hours & Attendance

Normal working hours shall be as prescribed by the Company — currently {{working_hours}} — subject to revision from time to time.

You may be required to work beyond normal hours, on weekends, or on public holidays based on exigencies of work without additional compensation, unless otherwise mandated by applicable law.

Punctuality and regular attendance are a condition of continued employment. Habitual late-coming, irregular attendance, or unauthorized absence shall render you liable to disciplinary action.

Leave Entitlements

You shall be entitled to leaves as per the Company's Leave Policy in force (Earned / Privilege Leave, Casual Leave, Sick Leave, and Maternity / Paternity Leave as per applicable statute). Leave shall be applied for in advance, except in emergencies, and shall be subject to approval. Unauthorized absence shall be treated as loss of pay and may result in disciplinary action.

Confidentiality & Non-Disclosure

You shall not, during or after your employment, directly or indirectly disclose, share, transfer, or misuse for personal benefit any Confidential Information of the Company, including trade secrets, financial records, client information, pricing strategies, HR and payroll data, business plans, and any other proprietary information.

All inventions, work products, reports, analyses, intellectual property, and software created by you during the course of employment shall be the exclusive property of the Company.

This confidentiality obligation shall survive termination of employment.

Non-Competition & Non-Solicitation

During the period of employment, you shall not, directly or indirectly, engage in, be employed by, or hold any interest in any business that competes with the Company, without prior written consent.

Code of Conduct & Policies

You are required to read, understand, and adhere to the Company's Code of Conduct, Anti-Sexual Harassment Policy, IT Acceptable Use Policy, Expense Reimbursement Policy, Health & Safety Policy, and all other policies as communicated from time to time.

Any violation of Company policies, rules, or the provisions of this Agreement shall render you liable to disciplinary action, up to and including termination of employment.

Notice Period & Separation

After confirmation in service, either party may terminate employment by providing {{notice_period}} days written notice to the other party.

You shall complete all handover formalities, return all Company property and assets, settle outstanding dues, and obtain departmental clearance prior to your last working day.

Full and Final Settlement, Relieving Letter, and Experience Certificate shall be processed and issued only after successful completion of exit formalities and clearance from all departments.

Gratuity & Retirement Benefits

Gratuity shall be payable in accordance with the Payment of Gratuity Act, 1972, upon completion of five (5) continuous years of service. Provident Fund and ESI benefits shall apply as per applicable statutes and salary eligibility.

Transfer & Mobility

The Company reserves the right to transfer you to any project site, department, office, or location, as necessitated by business requirements, with reasonable notice. Relocation support shall be provided in accordance with the Company's Transfer & Relocation Policy.

Performance Management

Your performance shall be reviewed periodically as per the Company's Performance Management System. Salary increments, promotions, and discretionary bonuses shall be merit-based, subject to business performance, and granted at Management's discretion.

Governing Law & Dispute Resolution

This Agreement and the employment relationship shall be governed by and construed in accordance with the laws of India. The parties shall first endeavour to resolve any dispute through internal escalation and good-faith negotiation before resorting to formal legal proceedings.

Entire Agreement

This Appointment Letter, the Employment Agreement, the Annexures hereto, and the Company's HR Policies together constitute the entire agreement between the parties with respect to the employment relationship and supersede all prior understandings, representations, and agreements, whether oral or written.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance", "marketing"],
        designation: ["manager", "executive", "senior-executive"],
        grade: ["grade-a", "grade-b", "grade-c"],
      },
    },
  ],

  offer: [
    {
      id: "offer-1",
      name: "Offer Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/OFF/{{employee_id}}          Date: {{joining_date}}

To,
{{employee_name}}

Letter of OFFER

Dear {{employee_name}},

We are pleased to offer you employment with {{company_name}} (hereinafter "the Company") on the terms and conditions set out herein, subject to your acceptance and satisfactory completion of pre-joining formalities.

Offer Details

Designation: {{designation}}
Department: {{department}}
Reporting Manager: {{reporting_manager}}
Proposed Date of Joining: {{joining_date}}
Work Location: {{work_location}}
Working Hours: {{working_hours}}

Compensation & Salary Structure

Your Cost to Company (CTC) will be ₹{{total_ctc}} per annum (₹{{total_ctc_monthly}} per month). The indicative component-wise salary breakup is as under:

{{salary_structure_table}}

Note: Statutory deductions shall apply as per law. A detailed Appointment Letter will be issued upon joining.

Probation & Notice

You shall be on probation for {{probation_period}} months from the date of joining. Notice period upon confirmation shall be {{notice_period}} days, or as stated in the Appointment Letter.

This offer remains valid until {{confirmation_date}}. Kindly confirm your acceptance in writing and return a signed copy of this letter.

We look forward to welcoming you to {{company_name}}.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance"],
        designation: ["manager", "executive"],
        grade: ["grade-a", "grade-b"],
      },
    },
  ],

  transfer: [
    {
      id: "transfer-1",
      name: "Transfer Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/TRF/{{employee_id}}          Date: {{transfer_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of TRANSFER

Dear {{employee_name}},

This is to inform you that you are being transferred within {{company_name}} as detailed below, with effect from {{transfer_date}}.

Transfer Details

Current Department: {{current_department}}
New Department: {{new_department}}
Current Designation: {{current_designation}}
New Designation: {{new_designation}}
Current Location: {{current_location}}
New Location: {{new_location}}
Reporting Manager: {{new_reporting_manager}}

Unless otherwise communicated in writing, your salary, grade and other benefits will remain unchanged. You are requested to report to your new department / location on {{transfer_date}} and complete the necessary handover formalities.

The Company reserves the right to transfer you to any project site, department, office, or location as necessitated by business requirements, with reasonable notice, in accordance with the Company's Transfer & Relocation Policy.

We appreciate your cooperation and continued contribution to {{company_name}}.`,
      assignment: { ...commonAssignment },
    },
  ],

  confirmation: [
    {
      id: "confirmation-1",
      name: "Confirmation Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/CNF/{{employee_id}}          Date: {{confirmation_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of CONFIRMATION

Dear {{employee_name}},

We are pleased to confirm that you have successfully completed your probation period and are now a confirmed employee of {{company_name}} with effect from {{confirmation_date}}.

Confirmation Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Confirmation Date: {{confirmation_date}}
Reporting Manager: {{reporting_manager}}
Work Location: {{work_location}}

Your performance during the probation period has been satisfactory. All other terms and conditions of your employment continue to apply as per your Appointment Letter and company policy.

Upon confirmation, the applicable notice period shall be {{notice_period}} days, or as stated in your Appointment Letter.

Congratulations, and we look forward to your continued contribution to {{company_name}}.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance", "marketing", "sales"],
        designation: ["executive", "senior-executive", "associate"],
        grade: ["grade-b", "grade-c", "grade-d"],
      },
    },
  ],

  experience: [
    {
      id: "experience-1",
      name: "Experience Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: `Yours sincerely,
For {{company_name}}

{{hr_manager_name}}
Human Resources`,
      content: `Ref No.: HR/EXP/{{employee_id}}          Date: {{leaving_date}}

To Whom It May Concern,

This is to certify that {{employee_name}} (Employee ID: {{employee_id}}) was employed with {{company_name}} from {{joining_date}} to {{leaving_date}} as {{designation}} in the {{department}} department.

During the period of employment, {{employee_name}} was found to be sincere, regular in attendance, and capable of discharging assigned responsibilities. {{employee_name}} left the organisation on {{leaving_date}} on account of {{leaving_reason}}.

We wish {{employee_name}} success in all future endeavours.`,
      assignment: { ...commonAssignment },
    },
  ],

  resignation: [
    {
      id: "resignation-1",
      name: "Resignation Acceptance (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/RES/{{employee_id}}          Date: {{resignation_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of RESIGNATION ACCEPTANCE

Dear {{employee_name}},

We acknowledge receipt of your resignation dated {{resignation_date}} from the position of {{designation}} and hereby accept the same.

Resignation Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Last Working Day: {{last_working_date}}
Notice Period: {{notice_period}} days
Reason stated: {{resignation_reason}}

You are requested to complete knowledge transfer, settle dues, and return all company property on or before your last working day. Full and Final Settlement, Relieving Letter, and Experience Certificate shall be issued subject to clearance from all departments.

We thank you for your contribution to {{company_name}} and wish you the very best.`,
      assignment: { ...commonAssignment },
    },
  ],

  promotion: [
    {
      id: "promotion-1",
      name: "Promotion Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/PRM/{{employee_id}}          Date: {{promotion_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of PROMOTION

Dear {{employee_name}},

We are pleased to inform you that, in recognition of your performance and contribution, you have been promoted as detailed below, with effect from {{promotion_date}}.

Promotion Details

Current Designation: {{current_designation}}
New Designation: {{new_designation}}
Department: {{department}}
Effective Date: {{promotion_date}}
Reporting Manager: {{new_reporting_manager}}

Revised compensation, if any, will be communicated separately by Human Resources. All other terms of employment remain unchanged unless stated otherwise in writing.

Congratulations on your well-deserved promotion.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance", "marketing", "sales"],
        designation: ["manager", "senior-manager", "director"],
        grade: ["grade-a", "grade-b"],
      },
    },
  ],

  "salary-increment": [
    {
      id: "salary-increment-1",
      name: "Salary Revision Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/REV/{{employee_id}}          Date: {{increment_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of SALARY REVISION

Dear {{employee_name}},

We are pleased to inform you that your compensation has been revised with effect from {{increment_date}}, in recognition of your performance and contribution to {{company_name}}.

Revision Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Effective Date: {{increment_date}}
Previous Basic (Monthly): ₹{{previous_basic_salary}}
Revised Basic (Monthly): ₹{{new_basic_salary}}
Previous CTC (Annual): ₹{{previous_total_ctc}}
Revised CTC (Annual): ₹{{new_total_ctc}}
Increment: {{increment_percentage}}%

The revised salary structure is as under:

{{salary_structure_table}}

The revised salary will be reflected in payroll from the effective date stated above. All other terms of employment remain unchanged.

Statutory deductions shall continue to apply as per law.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance", "marketing", "sales"],
        designation: ["manager", "senior-manager", "director", "executive"],
        grade: ["grade-a", "grade-b", "grade-c"],
      },
    },
  ],

  "probation-extension": [
    {
      id: "probation-extension-1",
      name: "Probation Extension (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/PRB/{{employee_id}}          Date: {{extension_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of PROBATION EXTENSION

Dear {{employee_name}},

This is to inform you that your probation period has been extended as detailed below, with effect from {{extension_date}}.

Extension Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Original Probation: {{original_probation_period}} months
Extension: {{extension_period}} months
Revised Probation End Date: {{new_probation_end_date}}
Reason: {{extension_reason}}

During the extended period, your performance will continue to be reviewed. We encourage you to work closely with your reporting manager and Human Resources for any support required.

All other terms of your employment remain unchanged as per your Appointment Letter.`,
      assignment: {
        branch: ["main-branch"],
        department: ["hr", "it", "finance", "marketing", "sales"],
        designation: ["executive", "senior-executive", "associate"],
        grade: ["grade-b", "grade-c", "grade-d"],
      },
    },
  ],

  termination: [
    {
      id: "termination-1",
      name: "Termination Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/TRM/{{employee_id}}          Date: {{termination_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of TERMINATION

Dear {{employee_name}},

This letter serves as formal notice of termination of your employment with {{company_name}}.

Termination Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Termination Date: {{termination_date}}
Last Working Day: {{last_working_date}}
Notice Period: {{notice_period}} days
Reason: {{termination_reason}}

You are required to complete handover, settle all dues, and return company property on or before your last working day. Full and Final Settlement will be processed as per company policy and applicable law.

We wish you the best for your future endeavours.`,
      assignment: { ...commonAssignment },
    },
  ],

  contract: [
    {
      id: "contract-1",
      name: "Contract Letter (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/CON/{{employee_id}}          Date: {{contract_start_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of FIXED-TERM CONTRACT

Dear {{employee_name}},

We are pleased to engage you on a fixed-term contract with {{company_name}} on the terms set out below.

Contract Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Contract Start Date: {{contract_start_date}}
Contract End Date: {{contract_end_date}}
Duration: {{contract_duration}} months
Reporting Manager: {{reporting_manager}}
Work Location: {{work_location}}
Working Hours: {{working_hours}}
Probation Period: {{probation_period}} months
Notice Period: {{notice_period}} days

Compensation & Salary Structure

Your Cost to Company (CTC) is ₹{{total_ctc}} per annum (₹{{total_ctc_monthly}} per month). The salary structure is as under:

{{salary_structure_table}}

Your engagement shall be governed by the Company's HR Policies, Code of Conduct, and applicable law. Please sign and return a duplicate of this letter to confirm your acceptance.`,
      assignment: { ...commonAssignment },
    },
  ],

  "contract-renewal": [
    {
      id: "contract-renewal-1",
      name: "Contract Renewal (Sample Format)",
      sampleVersion: LETTER_SAMPLES_VERSION,
      footer: signOff,
      content: `Ref No.: HR/CNR/{{employee_id}}          Date: {{new_contract_start_date}}

To,
{{employee_name}}
Employee ID: {{employee_id}}

Letter of CONTRACT RENEWAL

Dear {{employee_name}},

We are pleased to renew your fixed-term contract with {{company_name}} as detailed below.

Renewal Details

Employee ID: {{employee_id}}
Designation: {{designation}}
Department: {{department}}
Previous Contract End Date: {{previous_contract_end_date}}
Renewed Contract Start Date: {{new_contract_start_date}}
Renewed Contract End Date: {{contract_end_date}}
Renewal Duration: {{renewal_duration}} months
Reporting Manager: {{reporting_manager}}
Working Hours: {{working_hours}}
Notice Period: {{notice_period}} days

Compensation & Salary Structure

Your Cost to Company (CTC) continues at ₹{{total_ctc}} per annum (₹{{total_ctc_monthly}} per month), unless revised in writing. The salary structure is as under:

{{salary_structure_table}}

Unless otherwise stated, all previous terms and conditions of employment shall continue to apply. Please sign and return a duplicate of this letter to confirm acceptance.

We look forward to your continued contribution to {{company_name}}.`,
      assignment: { ...commonAssignment },
    },
  ],

  other: [],
};
